import { API_RESPONSE_TYPE } from '../../constants'
import { requestGoogleDriveAuthUrl } from './GoogleDriveApi'
import { clearGoogleDriveCache } from './GoogleDriveUploader'

const POPUP_WIDTH = 500
const POPUP_HEIGHT = 620
const POPUP_NAME = 'gdrive_oauth'
const POLL_INTERVAL_MS = 750
const GDRIVE_CONNECTED_MESSAGE = 'gdrive_connected'
const TRUSTED_MESSAGE_ORIGINS = [
  'https://okapis.io',
  'https://web.okcredit.in',
  'https://web.staging.okcredit.in'
]

const buildPopupFeatures = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }
  const left = Math.max(0, Math.floor((window.screen.width - POPUP_WIDTH) / 2))
  const top = Math.max(0, Math.floor((window.screen.height - POPUP_HEIGHT) / 2))
  return [
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'toolbar=no',
    'menubar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes'
  ].join(',')
}

export interface IOpenAuthPopupOptions {
  onAuthComplete?: () => void
}

const isTrustedOrigin = (origin: string): boolean => {
  if (typeof window !== 'undefined' && origin === window.location.origin) {
    return true
  }
  return TRUSTED_MESSAGE_ORIGINS.includes(origin)
}

export const openGoogleDriveAuthPopup = async ({
  onAuthComplete
}: IOpenAuthPopupOptions = {}): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false
  }

  const authWindow = window.open('', POPUP_NAME, buildPopupFeatures())
  if (!authWindow) {
    console.error('Google Drive auth popup was blocked by the browser')
    return false
  }

  const response = await requestGoogleDriveAuthUrl()
  if (response.type !== API_RESPONSE_TYPE.SUCCESS || !response.data) {
    authWindow.close()
    return false
  }

  try {
    authWindow.location.href = response.data
  } catch {
    authWindow.close()
    return false
  }

  if (onAuthComplete) {
    let settled = false
    let poll: number | null = null
    let messageListener: ((event: MessageEvent) => void) | null = null

    const cleanup = () => {
      if (settled) {
        return
      }
      settled = true
      if (poll !== null) {
        window.clearInterval(poll)
      }
      if (messageListener) {
        window.removeEventListener('message', messageListener)
      }
    }

    messageListener = (event: MessageEvent) => {
      if (!isTrustedOrigin(event.origin)) {
        return
      }
      if (
        event.data === GDRIVE_CONNECTED_MESSAGE ||
        event.data?.type === GDRIVE_CONNECTED_MESSAGE
      ) {
        try {
          authWindow.close()
        } catch {
          // ignore — popup may already be closing
        }
        cleanup()
        clearGoogleDriveCache()
        onAuthComplete()
      }
    }

    window.addEventListener('message', messageListener)

    poll = window.setInterval(() => {
      if (authWindow.closed) {
        cleanup()
        clearGoogleDriveCache()
        onAuthComplete()
      }
    }, POLL_INTERVAL_MS)
  }

  authWindow.focus()
  return true
}
